import { useState, useCallback, useMemo } from 'react';

// The names the discard prompts use, which must read as the section headings
// do. `person` and `familyNote` were missing, so the save footer's discard
// dialog (and the tab-close prompt that reuses it) fell back to the raw key
// and told a clinician "Unsaved changes to person will be lost"
// (TGCLOUD-1839). useDirtyState.test.js checks every key the sidebar marks
// has an entry here.
const SECTION_LABELS = {
  person: 'Person',
  diagnoses: 'Diseases',
  phenotypes: 'Phenotypes',
  genotypes: 'Genotypes',
  tags: 'Tags',
  externalFamilyIds: 'External IDs',
  familyNote: 'Family Note'
};

const INITIAL = {
  diagnoses: false,
  phenotypes: false,
  genotypes: false,
  tags: false,
  externalFamilyIds: false
};

export function useDirtyState() {
  const [dirty, setDirty] = useState(INITIAL);

  const markDirty = useCallback((section) => {
    setDirty((prev) => (prev[section] ? prev : { ...prev, [section]: true }));
  }, []);

  const markClean = useCallback((section) => {
    setDirty((prev) => (prev[section] ? { ...prev, [section]: false } : prev));
  }, []);

  const markAllClean = useCallback(() => setDirty(INITIAL), []);

  const isDirty = useCallback((section) => dirty[section] ?? false, [dirty]);

  const dirtyCount = useMemo(
    () => Object.values(dirty).filter(Boolean).length,
    [dirty]
  );

  const dirtySections = useMemo(
    () =>
      Object.entries(dirty)
        .filter(([, v]) => v)
        .map(([k]) => k),
    [dirty]
  );

  const dirtySectionLabels = useMemo(
    () => dirtySections.map((k) => SECTION_LABELS[k] ?? k),
    [dirtySections]
  );

  return {
    markDirty,
    markClean,
    markAllClean,
    isDirty,
    dirtyCount,
    dirtySections,
    dirtySectionLabels
  };
}
